<template>
  <div>
    <page-header
      :title="title"
      :container-class="containerClass"
      :info-text="'Select date range to fetch respective analytics'"
    />
    <div :class="containerClass"></div>
    <div class="page-section border-bottom-2">
      <div :class="containerClass">
        <div class="mt-3">
          <div>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <h5>Filter by Date(s)</h5>
                </div>

                <div>
                  <date-picker
                    v-model="filters.dateRange"
                    format="MMMM DD, YYYY"
                    valueType="YYYY-MM-DD"
                    style="width: 100%"
                    id="date"
                    lang="en"
                    placeholder="Filter by Start Date - End Date Range"
                    range
                    :clearable="false"
                    class="w-100 mb-3"
                    :input-attr="{ required: 'true' }"
                  >
                  </date-picker>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <h5>Filter by Status</h5>
                </div>
                <div class="">
                  <v-select
                    id="payment-status"
                    class="form-control v-select-custom"
                    label="label"
                    v-model="filters.paymentStatus"
                    :reduce="item => item.value"
                    :clearable="false"
                    placeholder="Select payment status"
                    :options="paymentStatusOptions"
                  >
                    <template #search="{ attributes, events }">
                      <input class="vs__search" v-bind="attributes" v-on="events" />
                    </template>
                    <template slot="option" slot-scope="option">
                      <div class="d-flex align-items-center">
                        <span>{{ option.label }}</span>
                      </div>
                    </template>
                  </v-select>
                </div>
              </div>
            </div>
          </div>
          <page-separator>
            <b-link v-b-toggle="'payments-over-time-section'" class="accordion__toggle p-0">
              <span class="flex">Payments Over Time</span>
              <md-icon class="accordion__toggle-icon">
                {{ paymentOverTimeVisible ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
              </md-icon>
            </b-link>
          </page-separator>
          <b-collapse
            id="payments-over-time-section"
            accordion="paymentsOverTimeAccordionId"
            :visible="true"
            class="accordion__menu"
            @hide="paymentOverTimeVisible = false"
            @show="paymentOverTimeVisible = true"
          >
            <div class="card border-left-0 mt-2">
              <div class="card-body">
                <div class="mb-1">
                  <div class="flex">
                    <div class="row">
                      <div class="col-lg-auto d-flex align-items-center">
                        <h5 class="card-title fw800" style="font-weight: bold;">Payments Over Time</h5>
                      </div>
                      <div class="col-lg d-flex mb-2 flex-wrap justify-content-end ">
                        <b-btn
                          :hidden="isMobileScreen"
                          variant="primary"
                          :disabled="isPaymentOverTimeRosterLoading || !this.isDateRangeValid()"
                          @click.prevent="fetchPaymentOverTimeRoster"
                          :style="`margin-top:${isMobileScreen ? '10px' : '0'}`"
                          class="btn-normal mb-2"
                        >
                          Export to Excel
                          <i v-if="isPaymentOverTimeRosterLoading" class="fas fa-circle-notch fa-spin ml-1"></i>
                          <span v-else class="material-icons icon-16pt ml-1">file_download</span>
                        </b-btn>
                      </div>
                    </div>
                    <p class="text-70">
                      View and export total payments. Use the date filter above to narrow down your selection based on
                      payment date. View and export total payment received/ due / overdue by class start date.
                    </p>
                    <div v-if="isMobileScreen" class="timeframe-select">
                      <b-btn
                        variant="primary"
                        :disabled="isPaymentOverTimeRosterLoading"
                        @click.prevent="fetchPaymentOverTimeRoster || !this.isDateRangeValid()"
                        style="width: 100%"
                        :style="`margin-top:${isMobileScreen ? '10px' : '0'}`"
                        class="btn-normal mb-2"
                      >
                        Export to Excel
                        <i v-if="isPaymentOverTimeRosterLoading" class="fas fa-circle-notch fa-spin ml-1"></i
                        ><span v-else class="material-icons icon-16pt ml-1">file_download</span>
                      </b-btn>
                    </div>

                    <div v-else>
                      <div class="d-flex mb-2" style="justify-content: space-between;">
                        <div class="d-flex flex-wrap"></div>
                      </div>
                    </div>

                    <div class="row">
                      <div class="col">
                        <div class="chart-container mt-4" style="position: relative; ">
                          <div v-if="isChartLoading">
                            <div class="text-center mt-5 mb-4">
                              <b-spinner
                                class="align-middle mr-2"
                                :style="{ color: getLoggedInUser.linked_entity.primary_color }"
                              ></b-spinner>

                              <strong>Loading...</strong>
                            </div>
                          </div>
                          <canvas v-else id="payment-graph" class="chart-container"></canvas>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-collapse>
          <page-separator>
            <b-link v-b-toggle="'payment-roster-section'" class="accordion__toggle p-0">
              <span class="flex">Payment Roster</span>
              <md-icon class="accordion__toggle-icon">
                {{ paymentRosterVisible ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
              </md-icon>
            </b-link>
          </page-separator>
          <b-collapse
            id="payment-roster-section"
            accordion="paymentRosterAccordionId"
            :visible="false"
            class="accordion__menu"
            @hide="paymentRosterVisible = false"
            @show="paymentRosterVisible = true"
          >
            <div class="card border-left-0 mt-2">
              <div class="card-body">
                <div class="d-flex mb-1">
                  <div class="flex">
                    <div class="d-flex align-items-center mb-3">
                      <h5 class="card-title fw800" style="font-weight: bold;">Export Payment Roster</h5>
                    </div>
                    <p class="text-70 mb-2">
                      View and export the list of students that has started the payment process. Use the date filter
                      above to narrow down your selection based on class startdate.
                    </p>
                    <div class="d-flex justify-content-end mt-3">
                      <b-btn
                        variant="primary"
                        :disabled="isExportLoading || !this.isDateRangeValid()"
                        @click.prevent="fetchPaymentRosters"
                        style="width: 200px"
                        class="btn-normal"
                      >
                        Export Roster <i v-if="isExportLoading" class="fas fa-circle-notch fa-spin ml-1"></i
                        ><span v-else class="material-icons icon-16pt ml-1">file_download</span>
                      </b-btn>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-collapse>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import vSelect from 'vue-select';
import Page from '@/components/Page.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { Chart } from 'chart.js';
import PageHeader from '@/components/Ui/PageHeader.vue';
import { mapActions, mapGetters } from 'vuex';
import { formatToAPIDate } from '../../../common/utils';
import moment from 'moment';

export default {
  components: { PageHeader, DatePicker, PageSeparator, vSelect },
  extends: Page,
  data: function() {
    return {
      title: 'Payment Reporting',
      filters: {
        dateRange: [],
        paymentStatus: 'paid',
      },
      paymentOverTimeVisible: true,
      isPaymentOverTimeRosterLoading: false,

      paymentStatusOptions: [
        { value: 'paid', label: 'Paid' },
        { value: 'unpaid', label: 'Unpaid' },
        { value: 'overdue', label: 'Overdue' },
      ],
      chartData: {
        type: 'bar',
        data: {},
        options: this.chartOptions,
      },
      isDateRangeEmpty: false,
      chartInstance: null,
      isChartLoading: false,
      windowWidth: window.innerWidth,
      paymentRosterVisible: false,
      isExportLoading: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['getLoggedInUser', 'getAuthToken']),

    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Payment Analytics', active: true },
      ];
    },
    isMobileScreen() {
      return window.innerWidth < 768;
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: true,
        indexAxis: 'x',
        plugins: {
          legend: {
            position: 'top',
          },
        },
        scales: {
          y: {
            beginAtZero: true,
          },
        },
      };
    },
  },
  watch: {
    'filters.dateRange': 'fetchAndUpdateChartData',
    'filters.paymentStatus': 'fetchAndUpdateChartData',
  },
  methods: {
    ...mapActions('payoutAcc', [
      'getSchoolPaymentOverTimeGraph',
      'getSchoolPaymentOverTimeRoster',
      'getSchoolPaymentRoster',
    ]),
    ...mapActions('fileDownload', ['downloadFile']),

    async fetchPaymentsOverTimeGraphData() {
      const resp = await this.getSchoolPaymentOverTimeGraph({
        start_date: this.filters.dateRange[0],
        end_date: this.filters.dateRange[1],
        payment_status: this.filters.paymentStatus || '',
      });
      return resp.data;
    },
    getDateRange(timeframeData) {
      if (timeframeData.date_from && timeframeData.date_to) {
        return `( ${timeframeData.date_from} - ${timeframeData.date_to} )`;
      }
      return '';
    },
    isDateRangeValid() {
      const [startDate, endDate] = this.filters.dateRange;

      if (!startDate || !endDate) return false;

      const start = new Date(startDate);
      const end = new Date(endDate);

      const monthDiff = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());

      return monthDiff <= 3;
    },
    async fetchChartData() {
      this.isChartLoading = true;
      if (!this.isDateRangeValid()) {
        this.isChartLoading = false;
        this.makeToast({ variant: 'danger', msg: 'Please select a date range of 3 months or less' });
        return;
      }
      const timeframeData = await this.fetchPaymentsOverTimeGraphData();
      const selectedStatus = this.paymentStatusOptions.find(option => option.value === this.filters.paymentStatus);
      const startDate = new Date(this.filters.dateRange[0]);
      const formattedStartDate = startDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      const endDate = new Date(this.filters.dateRange[1]);
      const formattedEndDate = endDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      var labels = [];
      if (timeframeData.labels && timeframeData.labels.length != 0) {
        labels = timeframeData.labels.map(label => {
          const [day, month, year] = label.split('-');
          const date = new Date(`${year}-${month}-${day}`);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          });
        });
      }
      var xLabel = '';
      if (this.isMobileScreen) {
        xLabel = this.filters.paymentStatus === 'paid' ? 'Transaction Date' : 'Class Date';
      } else {
        xLabel = this.filters.paymentStatus === 'paid' ? 'Transaction Date' : 'Class Start Date';
      }
      this.chartData = {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [
            {
              label: selectedStatus ? selectedStatus.label : 'All Payments',
              data: timeframeData.datasets,
              backgroundColor: this.getLoggedInUser.linked_entity.primary_color,
            },
          ],
        },
        options: {
          legend: {
            labels: {
              fontColor: this.getLoggedInUser.linked_entity.secondary_color,
            },
          },
          maintainAspectRatio: false,
          responsive: true,
          lineTension: 1,
          layout: { padding: { right: 25 } },
          scales: {
            yAxes: [
              {
                scaleLabel: {
                  display: true,
                  labelString: 'Payment Over Time ($)',
                },
                ticks: {
                  fontColor: this.getLoggedInUser.linked_entity.secondary_color,
                  beginAtZero: true,
                  padding: this.isMobileScreen ? 8 : 15,
                },
              },
            ],
            xAxes: [
              {
                scaleLabel: {
                  display: true,
                  labelString: xLabel + ' (' + formattedStartDate + ' - ' + formattedEndDate + ')',
                  fontStyle: 'italic',
                },
                ticks: {
                  fontColor: this.getLoggedInUser.linked_entity.secondary_color,
                  padding: this.isMobileScreen ? 8 : 15,
                },
              },
            ],
          },
        },
      };
      this.isChartLoading = false;
    },
    fetchAndUpdateChartData() {
      this.fetchChartData()
        .then(() => {
          const ctx = document.getElementById('payment-graph');
          if (this.chartInstance) {
            this.chartInstance.destroy();
          }

          this.chartInstance = new Chart(ctx, this.chartData);
        })
        .catch();
    },
    async downloadCSV(url) {
      try {
        await this.downloadFile({
          fileUrl: url,
        });
        const h = this.$createElement;
        this.makeToast({
          variant: 'success',
          msg: h('span', [h('i', { class: 'fas fa-download mr-2' }), 'File Downloaded']),
          toaster: 'b-toaster-bottom-left',
        });
      } catch (error) {
        //
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async fetchPaymentOverTimeRoster() {
      this.isPaymentOverTimeRosterLoading = true;
      const response = await this.getSchoolPaymentOverTimeRoster({
        start_date: this.filters.dateRange[0] ? this.filters.dateRange[0] : null,
        end_date: this.filters.dateRange[1] ? this.filters.dateRange[1] : null,
        time_frame: this.timeFrame,
        payment_status: this.filters.paymentStatus || '',
      });
      this.downloadCSV(response.data.url);
      this.isPaymentOverTimeRosterLoading = false;
    },
    async fetchPaymentRosters() {
      this.isExportLoading = true;
      if (!this.isDateRangeValid()) {
        this.isChartLoading = false;
        this.makeToast({ variant: 'danger', msg: 'Please select a date range of 3 months or less' });
        return;
      }
      const resp = await this.getSchoolPaymentRoster({
        start_date: this.filters.dateRange[0] ? this.filters.dateRange[0] : null,
        end_date: this.filters.dateRange[1] ? this.filters.dateRange[1] : null,
        payment_status: this.filters.paymentStatus || '',
      });
      this.downloadCSV(resp.data.url);
      this.isExportLoading = false;
    },
  },
  mounted() {
    this.filters.dateRange = [formatToAPIDate(moment().startOf('month')), formatToAPIDate(moment().endOf('month'))];
    this.fetchAndUpdateChartData();
  },
};
</script>
<style scoped>
.chart-container {
  width: 100%;
  height: 40vh;
}
@media only screen and (max-width: 700px) {
  .chart-container {
    width: 100%;
    height: 40vh;
  }
}
@media only screen and (max-width: 425px) {
  .chart-container {
    width: 250px;
    height: 250px;
  }
}
</style>
